import { useEffect, useState } from 'react';
import { useIsFetching, type UseQueryResult } from '@tanstack/react-query';
import type { BandwidthDashboardData } from './bandwidthDashboardTypes';

const MIN_VISIBLE_MS = 450;

/**
 * True while any bandwidth-dashboard query is refetching and the page already has data
 * (filter changes, row/cross-filter clicks, classic-tab drill queries). Hidden on first load.
 */
export function useBandwidthUpdatingIndicator(
  dashboardQuery: UseQueryResult<BandwidthDashboardData, Error>
): boolean {
  const bandwidthFetchingCount = useIsFetching({ queryKey: ['bandwidth-dashboard'] });

  const hasDisplayableData =
    dashboardQuery.data !== undefined || dashboardQuery.isPlaceholderData;

  const isRefetching = bandwidthFetchingCount > 0 && hasDisplayableData;

  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isRefetching) {
      setVisible(true);
      return undefined;
    }
    const timer = window.setTimeout(() => setVisible(false), MIN_VISIBLE_MS);
    return () => window.clearTimeout(timer);
  }, [isRefetching]);

  return visible;
}
